import {type DefaultError, type UseMutationOptions, useMutation} from '@tanstack/react-query'
import {createSuggestRepository} from '@yandex-delivery/api/repo/suggest'
import {createSuggestService} from '@yandex-delivery/api/service/suggest'
import {useI18n} from '@yandex-delivery/core/i18n'
import {get} from '@yandex-delivery/utils'
import {useMemo} from 'react'

import {useConfigContext} from '../../../../contexts'
import type {LocationDetectedVariant, LocationRequest, PickupPoints} from '../../model/types'
import {withCountryCode, withExternalApiPrefix} from '../../utils/fetchWith'
import {useFetch} from '../fetch'
import {fetchDetectLocationByCity} from './fetch-detect-location'
import {createPickupRouteService, fetchPickUpDropOffPoints, selectPickupPointParams} from './index'
import {toPickupPoints} from './mappers/toPickupPoints'
import type {FetchPickupPointsParams} from './types'

const useService = () => {
  const fetch = useFetch()
  const i18n = useI18n()
  const {countryCode} = useConfigContext()

  return useMemo(() => {
    const suggestRepository = createSuggestRepository({
      fetch: withCountryCode(countryCode, withExternalApiPrefix(fetch)),
    })

    return createPickupRouteService({
      params: createSuggestService(suggestRepository),
      fetch,
      countryCode,
      i18n,
    })
  }, [countryCode, fetch, i18n])
}

export const useFetchPickupPoints = (
  options?: Omit<UseMutationOptions<PickupPoints, DefaultError, FetchPickupPointsParams>, 'mutationFn'>,
) => {
  const {fetchPickupPoints} = useService()

  return useMutation({...options, mutationFn: fetchPickupPoints})
}

export function useFetchCityDesktop() {
  const fetch = useFetch()
  const {countryCode} = useConfigContext()

  return function fetchCity(params: LocationRequest) {
    return fetchDetectLocationByCity(params, fetch, countryCode)
  }
}

export function useFetchPickupPointsDesktop(isSource: boolean) {
  const fetch = useFetch()
  const {countryCode} = useConfigContext()
  const i18n = useI18n()
  const toPickupPointsBody = selectPickupPointParams(isSource)

  return useMutation({
    mutationFn(variant?: LocationDetectedVariant) {
      if (variant === undefined) {
        return Promise.resolve([])
      }

      return fetchPickUpDropOffPoints(
        toPickupPointsBody({
          geo_id: variant.geo_id,
          is_not_branded_partner_station: true,
        }),
        fetch,
        countryCode,
        i18n,
      )
        .then(toPickupPoints(undefined, i18n('ndd.pickup_points.schedule_everyday', 'Ежедневно')))
        .then(get('features'))
    },
  })
}
